import { Button } from '../ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip'
import { useNavigate } from 'react-router-dom'

import { GameRoomStatus, Room } from '@/redux/types/room'
import { EndGameMode } from '@/constants/room'
import StartGameButton from './StartGameButton'
import { EndGameButton } from './EndGameButton'
import { EndGameModal } from './EndGameModal'
import { UsersIcon } from '@heroicons/react/24/outline'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { GlobeAltIcon } from '@heroicons/react/24/outline'
import ROUTES from '@/routes/routes'
import ConfirmationModal from '../OGE/OGEDeletionModal'
import { useRoomControls } from '@/hooks/useRoomControls'
import StartSimulationButton from './StartSimulationButton'

interface RoomButtonsProps {
  roomId: string
  currentRoom: Room
}

const RoomButtons: React.FC<RoomButtonsProps> = ({ roomId, currentRoom }) => {
  const navigate = useNavigate()

  const {
    isEndGameModalOpen,
    setIsEndGameModalOpen,
    isEndingGame,
    isEndSimulationModalOpen,
    setIsEndSimulationModalOpen,
    isEndingSimulation,
    isResetModalOpen,
    setIsResetModalOpen,
    handleResetGame,
    handleResetClick,
    handleEndGame,
    handleEndSimulation,
  } = useRoomControls(roomId)

  const goToSimulationPlayers = () => {
    navigate(ROUTES.PROFESSOR.SIMULATION_PLAYERS(roomId))
  }

  const handleResetGameWrapper = async () => {
    await handleResetGame(currentRoom)
  }

  const handleEndGameWrapper = async () => {
    await handleEndGame(currentRoom)
  }

  const handleEndSimulationWrapper = async () => {
    await handleEndSimulation(currentRoom)
  }

  const handleResetClickWrapper = () => {
    handleResetClick(currentRoom)
  }

  const isGameCompleted = currentRoom?.status === GameRoomStatus.COMPLETED

  return (
    <div>
      <div className='mt-5'>
        <div className='flex gap-4 flex-wrap'>
          <StartGameButton
            currentRoom={currentRoom!}
            disabled={isGameCompleted}
            className={isGameCompleted ? 'opacity-50 cursor-not-allowed' : ''}
          />

          {(currentRoom?.status === GameRoomStatus.NOT_STARTED ||
            currentRoom?.is_simulating) && (
            <StartSimulationButton currentRoom={currentRoom!} />
          )}
          {currentRoom?.is_simulating && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className='text-white bg-purple-600 hover:bg-purple-700'
                    onClick={goToSimulationPlayers}
                  >
                    <div className='flex items-center gap-2'>
                      <UsersIcon className='h-[13px] w-[13px]' />
                      <span className='text-base'>View Players</span>
                    </div>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    View all players and open their game interface in new tabs
                    for testing
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {currentRoom?.status !== GameRoomStatus.NOT_STARTED &&
            !currentRoom.is_simulating && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className='text-white bg-dark-blue hover:bg-dark-blue-hovered'
                      onClick={handleResetClickWrapper}
                    >
                      <div className='flex items-center gap-2'>
                        <ArrowPathIcon className='h-[13px] w-[13px]' />
                        <span className='text-base'>Reset Game</span>
                      </div>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Reset the game to its initial state - this will delete all
                      conversations, negotiations, and decisions
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          {currentRoom?.status === GameRoomStatus.IN_PROGRESS &&
            !currentRoom.ends_in && (
              <EndGameButton
                onEndGame={() => {
                  if (currentRoom?.is_simulating) {
                    setIsEndSimulationModalOpen(true)
                  } else {
                    setIsEndGameModalOpen(true)
                  }
                }}
                disabled={
                  currentRoom?.is_simulating ? isEndingSimulation : isEndingGame
                }
                mode={
                  currentRoom?.is_simulating
                    ? EndGameMode.Simulation
                    : EndGameMode.Game
                }
              />
            )}
          {currentRoom?.world_graph_enabled && (
            <Button
              variant='outline'
              className='border-dark-blue text-dark-blue'
              onClick={() => navigate(ROUTES.PROFESSOR.ROOM_WORLD(roomId))}
            >
              <div className='flex items-center gap-2'>
                <GlobeAltIcon className='h-[13px] w-[13px]' />
                <span className='text-base'>World</span>
              </div>
            </Button>
          )}
        </div>
        <ConfirmationModal
          isOpen={isResetModalOpen}
          onClose={() => setIsResetModalOpen(false)}
          onConfirm={handleResetGameWrapper}
          title='Reset Game'
          description='Are you sure you want to reset this game? This will delete all conversations, negotiations, and decisions, and the game will start from the beginning.'
          confirmButtonText='Reset'
          cancelButtonText='Cancel'
        />
      </div>
      <EndGameModal
        isOpen={isEndGameModalOpen}
        onClose={() => setIsEndGameModalOpen(false)}
        onConfirm={handleEndGameWrapper}
        isLoading={isEndingGame}
        mode={EndGameMode.Game}
      />
      <EndGameModal
        isOpen={isEndSimulationModalOpen}
        onClose={() => setIsEndSimulationModalOpen(false)}
        onConfirm={handleEndSimulationWrapper}
        isLoading={isEndingSimulation}
        mode={EndGameMode.Simulation}
      />
      {currentRoom?.status === GameRoomStatus.IN_PROGRESS && (
        <>
          <div className='mt-2'>
            <div className='text-base'>
              <b>Started At:</b>{' '}
              {currentRoom.start_time
                ? new Date(currentRoom.start_time).toLocaleString('en-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    second: 'numeric',
                  })
                : '-'}
              <br />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default RoomButtons
